import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Key, Facebook, Youtube, Mail, ArrowRight, X, Sparkles, Menu, Check, Copy } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";
import { LOGO_URL, FOCUS_MODE, FOCUS_SESSION_PATH } from "./data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

// ============ SEO ============
export function SEO({ title, description, keywords, canonical, jsonLd }) {
  const site = "SnapshotFame";
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={canonical} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={site} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={LOGO_URL} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={LOGO_URL} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}

// ============ Nav ============
export function Nav() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const go = (path) => {
    setOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <nav data-testid="main-nav" className="fixed top-0 left-0 right-0 z-50 backdrop-blur-lg bg-[#2A1140]/80 border-b border-[#B58B2E]/15">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4 lg:px-12">
        <button onClick={() => go("/")} data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={LOGO_URL} alt="SnapshotFame Life Clarity Chic" className="h-11 w-auto group-hover:scale-105 transition-transform" />
          <div className="hidden sm:block text-left">
            <div className="serif text-lg text-[#F5D48A] leading-none">SnapshotFame<sup className="text-[10px]">™</sup></div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#A89FA8] mt-1">Life Clarity Chic</div>
          </div>
        </button>
        {FOCUS_MODE ? (
          <button onClick={() => go(FOCUS_SESSION_PATH)} className="btn-gold text-xs sm:text-sm px-5 py-2.5" data-testid="nav-focus-cta">Let&apos;s Talk Now</button>
        ) : (<>
        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => go("/mirror-lotus")} className="nav-link" data-testid="nav-cards">Mirror Lotus</button>
          <button onClick={() => go("/#sessions")} className="nav-link" data-testid="nav-sessions">Sessions</button>
          <button onClick={() => go("/about")} className="nav-link" data-testid="nav-about">About Dawn</button>
          <button onClick={() => go("/#connect")} className="btn-outline-gold" data-testid="nav-connect">Connect</button>
        </div>
        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="md:hidden text-[#F5D48A] p-2">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
        </>)}
      </div>
      {!FOCUS_MODE && open && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-[#B58B2E]/10 bg-[#2A1140]/95 px-6 py-6 flex flex-col gap-5">
          <button onClick={() => go("/mirror-lotus")} className="nav-link text-left" data-testid="nav-mobile-cards">Mirror Lotus</button>
          <button onClick={() => go("/#sessions")} className="nav-link text-left" data-testid="nav-mobile-sessions">Sessions</button>
          <button onClick={() => go("/about")} className="nav-link text-left" data-testid="nav-mobile-about">About Dawn</button>
          <button onClick={() => go("/#connect")} className="nav-link text-left" data-testid="nav-mobile-connect">Connect</button>
        </div>
      )}
    </nav>
  );
}

// ============ Footer ============
export function Footer() {
  return (
    <footer data-testid="footer" className="relative border-t border-[#B58B2E]/15 py-12 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="SnapshotFame" className="h-14 w-auto" />
            <div>
              <div className="serif text-lg text-[#F5D48A]">SnapshotFame™</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#A89FA8]">Life Clarity Chic · Dawn Delisle</div>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link to="/" className="text-xs text-[#A89FA8] tracking-widest uppercase hover:text-[#F5D48A]" data-testid="footer-home">Home</Link>
            <Link to="/about" className="text-xs text-[#A89FA8] tracking-widest uppercase hover:text-[#F5D48A]" data-testid="footer-about">About</Link>
            {!FOCUS_MODE && <Link to="/mirror-lotus" className="text-xs text-[#A89FA8] tracking-widest uppercase hover:text-[#F5D48A]" data-testid="footer-cards">Mirror Lotus</Link>}
            <Link to="/copyright" className="text-xs text-[#A89FA8] tracking-widest uppercase hover:text-[#F5D48A]" data-testid="footer-copyright">Copyright</Link>
            <Link to="/terms" className="text-xs text-[#A89FA8] tracking-widest uppercase hover:text-[#F5D48A]" data-testid="footer-terms">Terms</Link>
            <Link to="/privacy" className="text-xs text-[#A89FA8] tracking-widest uppercase hover:text-[#F5D48A]" data-testid="footer-privacy">Privacy</Link>
          </div>
        </div>
        <div className="border-t border-[#B58B2E]/15 pt-6 text-center space-y-2">
          <p className="text-xs text-[#A89FA8] tracking-wider" data-testid="footer-copyright-text">
            © {new Date().getFullYear()} SnapshotFame™ · Life Clarity Chic · Dawn Delisle · All Rights Reserved.
          </p>
          <p className="text-[10px] text-[#A89FA8]/70 tracking-wider max-w-2xl mx-auto leading-relaxed">
            All content, artwork, digital products, card designs, and written material on this site are the exclusive intellectual property of Dawn Delisle. Unauthorized copying, reproduction, distribution, or resale is strictly prohibited.
          </p>
        </div>
      </div>
    </footer>
  );
}

// ============ Divider ============
export function Divider() {
  return (
    <div className="sf-divider" aria-hidden="true">
      <span className="line"></span>
      <span className="dot"></span>
      <Key size={16} className="text-[#B58B2E]" />
      <span className="dot"></span>
      <span className="line"></span>
    </div>
  );
}

// ============ Checkout Modal (with real PayPal) ============
export function CheckoutModal({ product, onClose, onSubmit }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [step, setStep] = useState("details"); // details | pay | success | error
  const [paymentResult, setPaymentResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  if (!product) return null;

  const canPay = form.name.trim().length > 1 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const isDigitalSnapshot = ["financial-freedom", "love-connection", "dream-connection", "six-color-complete"].includes(product.id);

  const handleClose = () => {
    setStep("details");
    setPaymentResult(null);
    setErrorMsg("");
    setForm({ name: "", email: "" });
    onClose();
  };

  return (
    <div className="modal-backdrop" onClick={handleClose} data-testid="checkout-modal">
      <div className="modal-body" onClick={(e) => e.stopPropagation()}>
        {step !== "success" && (
          <div className="flex justify-between items-start mb-6">
            <div>
              <div className="section-label mb-2">Secure Checkout</div>
              <h3 className="serif text-3xl text-[#F2EFE8]" data-testid="checkout-product-name">{product.name}</h3>
              <p className="serif text-4xl gold-shimmer mt-2" data-testid="checkout-product-price">${product.price}</p>
            </div>
            <button onClick={handleClose} data-testid="checkout-close" className="text-[#A89FA8] hover:text-[#F5D48A] p-1">
              <X size={22} />
            </button>
          </div>
        )}

        {step === "details" && (
          <>
            <div className="bg-[#B58B2E]/10 border border-[#B58B2E]/25 rounded-sm p-4 mb-6">
              <p className="text-xs text-[#F5D48A] leading-relaxed">
                Enter your details, then complete payment through PayPal. {isDigitalSnapshot && <>You&apos;ll get your <strong>private access code</strong> instantly after paying.</>}
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="form-label">Your Name</label>
                <input required data-testid="checkout-name" className="form-input" placeholder="Full name"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Email</label>
                <input required type="email" data-testid="checkout-email" className="form-input" placeholder="you@email.com"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <button disabled={!canPay} onClick={() => { fireInitiateCheckout(product); setStep("pay"); }} className="btn-gold w-full mt-2 disabled:opacity-40" data-testid="checkout-continue">
                Continue to PayPal <ArrowRight size={14} />
              </button>
              <p className="text-[10px] text-center text-[#A89FA8] tracking-widest uppercase pt-2">
                Secured by PayPal · SSL Encrypted
              </p>
            </div>
          </>
        )}

        {step === "pay" && PAYPAL_CLIENT_ID && (
          <>
            <div className="mb-4 pb-4 border-b border-[#B58B2E]/25">
              <p className="text-xs text-[#A89FA8] uppercase tracking-widest mb-1">Paying as</p>
              <p className="text-[#F2EFE8]">{form.name}</p>
              <p className="text-[#F2EFE8]/70 text-sm">{form.email}</p>
            </div>
            <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                createOrder={async () => {
                  const r = await axios.post(`${API}/paypal/create-order`, {
                    product_id: product.id,
                    product_name: product.name,
                    price: parseFloat(product.price),
                    customer_name: form.name.trim(),
                    customer_email: form.email.trim(),
                  });
                  return r.data.id;
                }}
                onApprove={async (data) => {
                  try {
                    const r = await axios.post(`${API}/paypal/capture-order`, {
                      order_id: data.orderID,
                      product_id: product.id,
                      product_name: product.name,
                      price: parseFloat(product.price),
                      customer_name: form.name.trim(),
                      customer_email: form.email.trim(),
                    });
                    setPaymentResult(r.data);
                    setStep("success");
                    firePurchase(product, data.orderID);
                    if (onSubmit) onSubmit(product, form, r.data);
                  } catch (e) {
                    setErrorMsg("Your PayPal payment went through, but we hit a snag saving your order. Please email dawn to receive your access code.");
                    setStep("error");
                  }
                }}
                onError={() => {
                  setErrorMsg("PayPal encountered an error. Please try again or use a different payment method.");
                  setStep("error");
                }}
                onCancel={() => setStep("details")}
              />
            </PayPalScriptProvider>
            <button onClick={() => setStep("details")} className="text-xs tracking-widest uppercase text-[#A89FA8] hover:text-[#F5D48A] mt-4 w-full text-center" data-testid="checkout-back">
              ← Back to details
            </button>
          </>
        )}

        {step === "pay" && !PAYPAL_CLIENT_ID && (
          <p className="text-rose-300 text-sm text-center py-8">PayPal is not configured. Please contact Dawn.</p>
        )}

        {step === "success" && paymentResult && (
          <div className="text-center" data-testid="checkout-success">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-[#F5D48A]/20 p-4">
                <Check className="text-[#F5D48A]" size={40} />
              </div>
            </div>
            <div className="section-label mb-2">Payment Complete</div>
            <h3 className="serif text-4xl text-[#F2EFE8] mb-3">Thank you, {form.name.split(' ')[0]}!</h3>
            <p className="text-[#F2EFE8]/75 mb-8">Your {product.name} is ready.</p>

            {paymentResult.access_code && (
              <>
                <div className="sf-card jewel-card jewel-gold rounded-sm p-6 mb-6">
                  <div className="section-label mb-2">Your Private Access Code</div>
                  <div className="flex items-center justify-center gap-3">
                    <p className="serif text-3xl gold-shimmer tracking-widest" data-testid="access-code">{paymentResult.access_code}</p>
                    <button onClick={() => navigator.clipboard.writeText(paymentResult.access_code)}
                      className="text-[#F5D48A] hover:text-white p-2" data-testid="copy-code" title="Copy">
                      <Copy size={16} />
                    </button>
                  </div>
                  <p className="text-[10px] text-[#A89FA8] tracking-widest uppercase mt-3">Keep this code — you&apos;ll need it every time</p>
                </div>
                <a href={paymentResult.experience_url} className="btn-gold w-full mb-3" data-testid="enter-experience">
                  Enter Your Experience Now <ArrowRight size={14} />
                </a>
              </>
            )}

            <p className="text-xs text-[#A89FA8] mt-4">A receipt has been sent to <span className="text-[#F5D48A]">{form.email}</span> by PayPal.</p>
            <button onClick={handleClose} className="text-xs tracking-widest uppercase text-[#A89FA8] hover:text-[#F5D48A] mt-6 w-full">Close</button>
          </div>
        )}

        {step === "error" && (
          <div className="text-center">
            <p className="text-rose-300 mb-4">{errorMsg}</p>
            <button onClick={() => setStep("details")} className="btn-gold" data-testid="retry">Try Again</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Toast({ msg, onClose }) {
  useEffect(() => {
    if (msg) { const t = setTimeout(onClose, 4200); return () => clearTimeout(t); }
  }, [msg, onClose]);
  if (!msg) return null;
  return <div className="toast-good" data-testid="toast">{msg}</div>;
}

// ============ Buy Button ============
export function BuyButton({ product, onBuy, label = "Continue" }) {
  return (
    <button onClick={() => onBuy(product)} className="btn-paypal max-w-sm" data-testid={`buy-${product.id}`}>
      <span className="font-bold italic">Pay</span>
      <span className="font-bold italic text-[#003087]">Pal</span>
      <span className="mx-1">·</span>
      <span>{label}</span>
      <ArrowRight size={16} />
    </button>
  );
}

// ============ Email Subscribe ============
export function FridayMirror() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | already | error
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/subscribers`, { email, source: "friday-mirror" });
      setStatus("success");
      setMsg("You're in. Look for your first Mirror Question this Friday.");
      setEmail("");
      if (typeof window.gtag === "function") {
        window.gtag("event", "sign_up", { method: "friday-mirror" });
      }
      if (typeof window.fbq === "function") {
        window.fbq("track", "Subscribe", {
          content_name: "Friday Mirror",
          currency: "USD",
          value: 0.00,
          predicted_ltv: 50.00,
        });
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        setStatus("already");
        setMsg("You're already on the list — thank you.");
      } else {
        setStatus("error");
        setMsg("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <section id="friday-mirror" data-testid="section-friday-mirror" className="relative py-24 lg:py-32 px-6 lg:px-12">
      <div className="max-w-3xl mx-auto text-center relative">
        <div className="aura" style={{ width: 500, height: 500, top: "-100px", left: "50%", transform: "translateX(-50%)", opacity: 0.4 }}></div>
        <div className="relative z-10">
          <div className="flex justify-center mb-6">
            <Sparkles className="text-[#F5D48A]" size={28} />
          </div>
          <div className="section-label" data-testid="friday-label">The Friday Mirror</div>
          <h2 className="display-serif text-4xl sm:text-5xl md:text-6xl mb-6" data-testid="friday-heading">
            One free <em>Mirror Question,</em><br/>every Friday.
          </h2>
          <p className="text-[#F2EFE8]/70 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
            A single, quiet question — delivered to your inbox — designed to reveal one thing you didn&apos;t know you were repeating.
            <br/><span className="text-[#A89FA8] text-sm">No spam. Unsubscribe anytime.</span>
          </p>

          {status === "success" || status === "already" ? (
            <div className="sf-card rounded-sm p-8 max-w-lg mx-auto" data-testid="friday-success">
              <Sparkles className="mx-auto mb-3 text-[#F5D48A]" size={22} />
              <p className="serif text-2xl text-[#F5D48A] mb-2">Beautifully done.</p>
              <p className="text-[#F2EFE8]/75">{msg}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="max-w-lg mx-auto flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                data-testid="friday-email-input"
                className="form-input flex-1"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button type="submit" disabled={status === "sending"} className="btn-gold whitespace-nowrap" data-testid="friday-submit">
                {status === "sending" ? "Joining..." : "Send Me Fridays"}
              </button>
            </form>
          )}
          {status === "error" && <p className="text-rose-300 text-sm mt-3" data-testid="friday-error">{msg}</p>}
        </div>
      </div>
    </section>
  );
}

// ============ Analytics helper ============
export function fireLeadEvent(product) {
  if (typeof window.gtag === "function") {
    window.gtag("event", "generate_lead", {
      currency: "USD",
      value: product.price,
      items: [{ item_id: product.id, item_name: product.name, price: product.price }],
    });
  }
  if (typeof window.fbq === "function") {
    window.fbq("track", "Lead", {
      content_name: product.name,
      content_ids: [product.id],
      value: product.price,
      currency: "USD",
    });
  }
}

export function fireViewContent(product) {
  if (typeof window.fbq === "function") {
    window.fbq("track", "ViewContent", {
      content_name: product.name,
      content_ids: [product.id],
      content_type: "product",
      value: product.price,
      currency: "USD",
    });
  }
  if (typeof window.gtag === "function") {
    window.gtag("event", "view_item", {
      currency: "USD",
      value: product.price,
      items: [{ item_id: product.id, item_name: product.name, price: product.price }],
    });
  }
}

export function fireInitiateCheckout(product) {
  if (typeof window.fbq === "function") {
    window.fbq("track", "InitiateCheckout", {
      content_name: product.name,
      content_ids: [product.id],
      content_type: "product",
      value: product.price,
      currency: "USD",
      num_items: 1,
    });
  }
  if (typeof window.gtag === "function") {
    window.gtag("event", "begin_checkout", {
      currency: "USD",
      value: product.price,
      items: [{ item_id: product.id, item_name: product.name, price: product.price }],
    });
  }
}

export function firePurchase(product, orderId) {
  if (typeof window.fbq === "function") {
    window.fbq("track", "Purchase", {
      content_name: product.name,
      content_ids: [product.id],
      content_type: "product",
      value: product.price,
      currency: "USD",
      num_items: 1,
    });
  }
  if (typeof window.gtag === "function") {
    window.gtag("event", "purchase", {
      transaction_id: orderId || product.id,
      currency: "USD",
      value: product.price,
      items: [{ item_id: product.id, item_name: product.name, price: product.price }],
    });
  }
}
